import { Row, Button, Form, Col } from "react-bootstrap";
import { RootState } from "redux/store";
import { useAppSelector, useAppDispatch } from "redux/hooks";
import { BulkEdit } from "./Popups/BulkEdit";
import { useCallback, useEffect, useState } from "react";
import { getStores } from "redux/slices/Stores";
import { updateAssignPriceGroupField, getPriceGroupsStore, modifyPriceGroupsStore, applyPriceGroupsStore, addSelectedRows, addDeselectedRows, updateAssignPriceGroupTableField, getPriceGroupsStoreSimple } from "redux/slices/AssignPriceGroup";
import { useDisplayError } from "helpers/Functions";
import { calculateGP, calculateMarkup, displayFloatPrice } from "helpers/data/dataHandling";
import { getDepartments } from "redux/slices/Departments";
import { Spinner, Table, Modal, ProTable } from "components";
import { getSubDepartments } from "redux/slices/SubDepartment";
import { getStockGroups } from "redux/slices/StockGroups";
import { PriceGroupsStoreAddUpdate } from "models/dbModels/priceGroups/PriceGroupsStore";

type stockProps = {
    display: boolean;
    updateTab: (tab: string) => void;
}
export default function Stock({ display, updateTab }: stockProps) {
    const dispatch = useAppDispatch();
    const displayError = useDisplayError();
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [stockCount, setStockCount] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [bulkEditShow, setBulkEditShow] = useState(false);
    const [disableModal, setDisableModal] = useState(false);
    const stores = useAppSelector((state: RootState) => state.stores);
    const stockGroups = useAppSelector((state: RootState) => state.stockGroups);
    const priceGroups = useAppSelector((state: RootState) => state.priceGroups);
    const departments = useAppSelector((state: RootState) => state.department);
    const subDepartments = useAppSelector((state: RootState) => state.subDepartment);
    const storeGroupId = useAppSelector((state: RootState) => state.global.storeGroupId);
    const assignPriceGroup = useAppSelector((state: RootState) => state.assignPriceGroup);
    const user = useAppSelector((state: RootState) => state.logedinUser);

    const columns = [
        {
            Header: "Name",
            accessor: "managedStockName",
            defaultCanSort: true,
            width: 190,
        },
        {
            Header: "Cost",
            accessor: "costPrice",
            defaultCanSort: true,
            width: 80,
            Cell: (row: any) => displayFloatPrice(row.value),
        },
        {
            Header: "Base Sell Price",
            accessor: "basePrice",
            defaultCanSort: true,
            width: 100,
            Cell: (row: any) => displayFloatPrice(row.value),
        },
        {
            Header: "Current GP%",
            accessor: "gp",
            // defaultCanSort: true,
            width: 100,
            Cell: (row: any) => {
                const sellPrice = row.cell.row.original.currentPrice || 0;
                const costPrice = row.cell.row.original.costPrice || 0;
                const gstCode = row.cell.row.original.gstCode || 'Y';
                return <span>{calculateGP(sellPrice, costPrice, gstCode)}</span>;
            }
        },
        {
            Header: "Current Sell Price",
            accessor: "currentPrice",
            defaultCanSort: true,
            width: 120,
            Cell: (row: any) => displayFloatPrice(row.value),
        },
        {
            Header: "Current Price Group",
            accessor: "priceGroupName",
            defaultCanSort: true,
            width: 120,
        },
        {
            Header: "New GP%",
            accessor: "gps",
            // defaultCanSort: true,
            width: 100,
            Cell: (row: any) => {
                const sellPrice = row.cell.row.original.newPrice || 0;
                const costPrice = row.cell.row.original.costPrice || 0;
                const gstCode = row.cell.row.original.gstCode || 'Y';
                return <span>{calculateGP(sellPrice, costPrice, gstCode)}</span>;
            }
        },
        {
            Header: "New Sell Price",
            accessor: "newPrice",
            defaultCanSort: true,
            width: 120,
            Cell: (row: any) => displayFloatPrice(row.value),
        },
        {
            Header: "New Price Group",
            accessor: "newPriceLevelId",
            defaultCanSort: true,
            width: 200,
            Cell: (row: any) => <PriceGroupDropdown id={row.cell.row.original.newPriceGroupId} data={row.cell.row.original} />,
        },
    ];

    useEffect(() => {
        setStockCount(assignPriceGroup.selectedStocks.reduce((accumulator, page: any) => accumulator + page.length, 0));
    }, [assignPriceGroup.selectedStocks]);

    useEffect(() => {
        dispatch(getDepartments());
    }, []);

    useEffect(() => {
        if (display === true && assignPriceGroup.currentStore > 0 && assignPriceGroup.activeTab === 3) getStocksList();
        if (assignPriceGroup.currentDepartment === 0) {
            dispatch(updateAssignPriceGroupField({ key: "currentSubDepartment", value: 0 }));
        } else {
            dispatch(getSubDepartments(assignPriceGroup.currentDepartment));
        }
    }, [assignPriceGroup.currentStore, assignPriceGroup.currentDepartment, display, assignPriceGroup.currentSubDepartment, assignPriceGroup.activeTab, assignPriceGroup.managedStockGroupId, storeGroupId]);

    useEffect(() => {
        if (assignPriceGroup.currentStore === 0 && assignPriceGroup.activeTab === 3) {
            dispatch(updateAssignPriceGroupField({ key: "error", value: "Please select a store to continue." }));
        } else {
            dispatch(updateAssignPriceGroupField({ key: "error", value: "" }));
        }
    }, [assignPriceGroup.currentStore, assignPriceGroup.activeTab]);

    async function getStocksList() {
        setLoading(true);
        if (assignPriceGroup.currentStore > 0) {
            dispatch(getStores({ storeGroupIds: [storeGroupId] }));
            dispatch(getDepartments());
            dispatch(getStockGroups());
            dispatch(getPriceGroupsStore({
                groupBy: "None",
                storeIds: [assignPriceGroup.currentStore],
                managedStockDepartmentIds: (assignPriceGroup.currentDepartment > 0 ? [assignPriceGroup.currentDepartment] : undefined),
                managedStockSubdepartmentIds: (assignPriceGroup.currentSubDepartment > 0 ? [assignPriceGroup.currentSubDepartment] : undefined),
                managedStockStockGroupIds: (assignPriceGroup.managedStockGroupId > 0 ? [assignPriceGroup.managedStockGroupId] : undefined),
                ...( assignPriceGroup.keyword && { managedStockKeyword: assignPriceGroup.keyword} ),
                managedStockContains: true,
            })).then((res) => {
                if (res.data) {
                    let stocks = res.data.sort((a, b) => a.managedStockName.localeCompare(b.managedStockName));
                    dispatch(updateAssignPriceGroupField({ key: "stocks", value: stocks.map((stock: any) => { return { ...stock, id: stock.managedStockId } }) }));
                }
            });
        }
        setLoading(false);
    }

    async function handleSubmit() {
        setSaving(true);
        setDisableModal(true);
        let stockIds: number[] = [];
        if (assignPriceGroup.selectedStocks.length === 0) {
            stockIds = assignPriceGroup.stocks.map((stock: any) => stock.managedStockId);
        } else {
            //@ts-ignore
            stockIds = assignPriceGroup.selectedStocks.flatMap((stockId: any) => stockId.map(i => i.id));
        }
        const payload: any = {
            filter: {
                storeIds: [assignPriceGroup.currentStore],
                managedDepartmentIds: assignPriceGroup.currentDepartment !== 0 ? [assignPriceGroup.currentDepartment] : [],
                managedStockIds: stockIds,
            },
            sync: true,
        }
        await dispatch(applyPriceGroupsStore(payload)).then((res) => {
            setShowModal(false);
            setDisableModal(false);
            setSaving(false);
            if(res.data){
                getStocksList();
                handleChange("selectedStocks", []);
            } 
        });
    }

    const PriceGroupDropdown = (props: any) => {
        const [priceGroup, setPriceGroup] = useState(props.id ? props.id : "Mixed");
        const [loadingValue, setLoadingValue] = useState(false);
        async function handlePriceGroupUpdate(priceGroupId: number, managedStockId: number[]) {
            setLoadingValue(true);
            let payload: PriceGroupsStoreAddUpdate[] = [];
            managedStockId.forEach((stockId: number) => {
                payload.push({
                    storeId: assignPriceGroup.currentStore,
                    managedStockId: stockId,
                    newPriceGroupId: priceGroupId
                })
            });
            await dispatch(modifyPriceGroupsStore(payload)).then((res) => {
                if (res.data) {
                    setPriceGroup(priceGroupId);
                    dispatch(getPriceGroupsStoreSimple({
                        groupBy: "None",
                        storeIds: [assignPriceGroup.currentStore],
                        managedStockIds: managedStockId
                    })).then(res => {
                        let stocks: any[] = [...assignPriceGroup.stocks];
                        res.data.forEach((item: any) => {
                            const index = stocks.findIndex((stock: any) => stock.managedStockId === item.managedStockId);
                            if (index >= 0) stocks[index] = { ...item, id: item.managedStockId };
                        })
                        dispatch(updateAssignPriceGroupField({ key: "stocks", value: stocks }));
                    })
                }
            });
            setLoadingValue(false);
        }

        return (
            <div style={{ maxWidth: "250px", }}>
                {loadingValue ?
                    <span><Spinner className="spinner-border-sm me-1" tag="span" color="primary" />Saving... </span>
                    :
                    <Form.Group style={{ margin: "-3px 0px" }}>
                        <Form.Select
                            disabled={!user.permissions.assignPriceGroups.canUpdate}
                            className="custom-select"
                            value={priceGroup}
                            onChange={(e) => handlePriceGroupUpdate(parseInt(e.target.value), [props.data.managedStockId])}
                        >
                            <option value={0}></option>
                            <option value={-1}>Base Sell Price</option>
                            <option value={-2} hidden>Mixed</option>
                            {priceGroups.table.map((priceLevel: any, index: any) => {
                                return <option key={index} value={priceLevel.id}>{priceLevel.name}</option>
                            })}
                        </Form.Select>
                    </Form.Group>
                }
            </div>
        );
    };

    function TopbarChildComponent() {
        return <>{stockCount > 1 &&
            <>
                <Button disabled variant="light" className="me-1">{stockCount} Selected</Button>
                <Button variant="light" className="me-1" onClick={() => setBulkEditShow(true)}><i className="mdi mdi-pencil"></i> Bulk Edit</Button>
            </>
        }</>;
    }

    async function handleBulkPriceGroupsUpdate(priceGroupId: number, managedStockId: number[]) {
        let payload: any = [];
        managedStockId.forEach((stockId: number) => {
            payload.push({
                storeId: assignPriceGroup.currentStore,
                managedStockId: stockId,
                newPriceGroupId: priceGroupId
            })
        });
        await dispatch(modifyPriceGroupsStore(payload)).then((res) => {
            if (res.data) {
                handleChange("selectedStocks", []);
                handleChange("deselectedStocks", []);
            }
        });
    }

    async function saveBulkEdit(priceGroupId: number) {
        let stockIds: number[] = [];
        if (stockCount === 0) {
            stockIds = assignPriceGroup.stocks.map((stock: any) => stock.managedStockId);
        } else {
            assignPriceGroup.selectedStocks.forEach((page: any) =>
                page.forEach((stock: any) => stockIds.push(stock.id))
            );
        }
        await handleBulkPriceGroupsUpdate(priceGroupId, stockIds);
        await getStocksList();
        setBulkEditShow(false);
    }

    function handleChange(key: string, value: any) {
        dispatch(updateAssignPriceGroupField({ key: key, value: value }));
    }

    function selectedRowsUpdate(rows: any, index: any) {
        dispatch(addSelectedRows({ rows: rows, index: index }));
    }

    function deselectedRowsUpdate(rows: any, index: any) {
        dispatch(addDeselectedRows({ rows: rows, index: index }));
    }

    function updateTableRows(dataTableState: any) {
        handleChange("pageIndex", dataTableState.pageIndex);
        handleChange("pageSize", dataTableState.pageSize);
        dispatch(getPriceGroupsStore({
            groupBy: "None",
            storeIds: [assignPriceGroup.currentStore],
            offset: dataTableState.pageIndex * dataTableState.pageSize,
            managedStockDepartmentIds: (assignPriceGroup.currentDepartment > 0 ? [assignPriceGroup.currentDepartment] : undefined),
            managedStockSubdepartmentIds: (assignPriceGroup.currentSubDepartment > 0 ? [assignPriceGroup.currentSubDepartment] : undefined),
            managedStockKeyword: assignPriceGroup.keyword,
            managedStockContains: true,
        }));
    }

    function onSearchClick(keyword: string) {
        handleChange("pageIndex", 0);
        handleChange("keyword", keyword);
        dispatch(getPriceGroupsStore({
            groupBy: "None",
            storeIds: [assignPriceGroup.currentStore],
            managedStockDepartmentIds: (assignPriceGroup.currentDepartment > 0 ? [assignPriceGroup.currentDepartment] : undefined),
            managedStockSubdepartmentIds: (assignPriceGroup.currentSubDepartment > 0 ? [assignPriceGroup.currentSubDepartment] : undefined),
            managedStockKeyword: assignPriceGroup.keyword,
            managedStockContains: true,
        }));
    }

    return (
        <>
            <Row className="mb-2">
                <Col xs={3}>
                    <Form.Group>
                        <Form.Label className="form-check-label">Store</Form.Label>
                        <Form.Select
                            className="custom-select"
                            value={assignPriceGroup.currentStore}
                            onChange={(e) => dispatch(updateAssignPriceGroupField({ key: "currentStore", value: parseInt(e.target.value) }))}
                        >
                            <option hidden value="0">Choose a Store</option>
                            {stores.table.map((priceLevel: any, index: any) => {
                                return <option key={index} value={priceLevel.id}>{priceLevel.name}</option>
                            })}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col xs={3}>
                    <Form.Group>
                        <Form.Label className="form-check-label">Department</Form.Label>
                        <Form.Select
                            className="custom-select"
                            value={assignPriceGroup.currentDepartment}
                            onChange={(e) => dispatch(updateAssignPriceGroupField({ key: "currentDepartment", value: parseInt(e.target.value) }))}
                        >
                            <option value="0">All</option>
                            {departments.table.map((department: any, index: any) => {
                                return <option key={index} value={department.id}>{department.name}</option>
                            })}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col xs={3}>
                    <Form.Group>
                        <Form.Label className="form-check-label">Subdepartment</Form.Label>
                        <Form.Select
                            className="custom-select"
                            value={assignPriceGroup.currentSubDepartment}
                            onChange={(e) => dispatch(updateAssignPriceGroupField({ key: "currentSubDepartment", value: parseInt(e.target.value) }))}
                        >
                            <option value="0">All</option>
                            {assignPriceGroup.currentDepartment !== 0 && subDepartments.table.map((department: any, index: any) => {
                                return <option key={index} value={department.id}>{department.name}</option>
                            })}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col xs={3}>
                    <Form.Group>
                        <Form.Label className="form-check-label">Stock Groups</Form.Label>
                        <Form.Select
                            className="custom-select"
                            value={assignPriceGroup.managedStockGroupId ?? ""}
                            onChange={(e) => dispatch(updateAssignPriceGroupField({ key: "managedStockGroupId", value: parseInt(e.target.value) }))}
                        >
                            <option value="0">All</option>
                            {stockGroups.table.map((department: any, index: any) => {
                                return <option key={index} value={department.id}>{department.name}</option>
                            })}
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>
            <Row className="mb-1 ps-1 pe-1">
                <div className="page-container">
                    {bulkEditShow && <BulkEdit
                        show={bulkEditShow}
                        saveSelected={saveBulkEdit}
                        handleClose={() => setBulkEditShow(false)}
                        count={stockCount}
                    />}
                    <Modal
                        title="Confirm - Stock level"
                        show={showModal}
                        buttonType="primary"
                        disableButtons={disableModal}
                        buttonText={disableModal ? "Applying..." : "Continue"}
                        handleClose={() => setShowModal(false)}
                        handleSubmit={handleSubmit}
                        description={
                            <>
                                <p>You are about to apply the new price group and sync the new sell prices.</p>
                                <p>Are you sure you want to continue?</p>
                            </>
                        }
                    />
                    <ProTable
                        data={assignPriceGroup}
                        pageSize={100}
                        isSortable={true}
                        pagination={true}
                        table={assignPriceGroup.stocks}
                        columns={columns}
                        loading={assignPriceGroup.loading}
                        updateTableRows={updateTableRows}
                        // Search Props 
                        isSearchable={true}
                        // searchPlaceHolder={`by Name ${stocks.searchPdes ? "/ PDE" : ""} ${stocks.searchBarcodes ? "/ APN" : ""}`}
                        onSearchClick={onSearchClick}
                        // Toolbar Propse)}
                        reloadTableRows={() => getStocksList()}
                        // Select Props
                        isSelectable={true}
                        selectedRows={assignPriceGroup.selectedStocks}
                        deselectedRows={assignPriceGroup.deselectedStocks}
                        selectedRowsUpdate={selectedRowsUpdate}
                        deselectedRowsUpdate={deselectedRowsUpdate}
                        // Extra Components Props
                        topbarChild={<TopbarChildComponent />}
                    />
                </div>
            </Row>
            <Row className="mt-3 mb-2">
                <Col sm={6}>
                </Col>
                <Col sm={6} className="d-flex flex-row-reverse">
                    <Button variant="primary" className="btn ms-1 cta-button" onClick={() => { setShowModal(true) }} disabled={!user.permissions.assignPriceGroups.canUpdate}>
                        {saving ? <Spinner className="spinner-border-sm me-1" tag="span" color="white" /> : null}
                        {saving ? "Applying..." : "Apply & Sync"}
                    </Button>
                </Col>
            </Row>
        </>
    );
}